import { loadThree, type ThreeNS } from "../../wakbbu/engine3d/three";
import { vibrate } from "../../wakbbu/engine/haptics";
import { MallangAudio } from "./mallangAudio";
import { MAX_DENTS, MAX_GRABS, makeMallangUniforms, mallangOnBeforeCompile, type MallangUniforms } from "./mallangShader";

type V3 = [number, number, number];

export type MallangCallbacks = {
  onSquish: (count: number) => void;
  onAudioState: (running: boolean) => void;
  /** iPhone: tilting and shaking need a tap on "흔들기 켜기" first (the browser asks for motion access) */
  onMotionNeeded: (needed: boolean) => void;
};

/** How deep a finger can sink (fraction of the radius): far, but it gets stiffer the deeper it goes. */
const DMAX = 0.34;
/** a finger's contact patch on screen (CSS px) when the browser does not report one: the dent's width */
const CONTACT_PX = 34;
/** at most this many fingers on it at once */
const MAX_PRESS = 4;
/** physics substep (s): the springs ring at up to ~9 Hz, so a fine step keeps them stable */
const H = 1 / 240;
/**
 * It is a rice cake (인절미) turned memory foam: heavy and sticky, slow to give and slow to come back, barely
 * bouncing. A released dent: only a little springs back at once (well damped), the rest creeps back in (~4 s).
 */
const FAST_SHARE = 0.05;
const FAST_W = 2 * Math.PI * 3, FAST_Z = 1; // critically damped: it never rings
/** memory-foam refill: time constant (s), and how long it takes to get going */
const SLOW_TAU = 1.7, SLOW_START = 0.5;
/** a lifted finger: the sticky cake clings to it this long (s), pulled up by this share of the dent, then lets go (쩍) */
const STICK_T = 0.2, PULL_K = 0.4;
const PULL_W = 2 * Math.PI * 9, PULL_Z = 0.3;
/** pulling it out like a rice cake: how far (radius units; it gets harder the further it goes), and how far behind
 * the finger the heavy cake follows (s) */
const PULL_MAX = 2.6, PULL_LAG = 0.11;
/** how far the strand's middle sags under its own weight, per unit of pull */
const STRAND_SAG = 0.22;
/** a stretch let go: it clings to the finger a moment, a little draws back at once (no ring), the rest creeps back in */
const GRAB_STICK = 0.18;
const GRAB_FAST = 0.08, GRAB_W = 2 * Math.PI * 2.5, GRAB_Z = 1;
const GRAB_TAU = 1.6, GRAB_START = 0.5;
/** the whole body's jiggle (a P2 mode), the press-squash, the pinch squeeze */
const WOB_W = 2 * Math.PI * 4, WOB_Z = 0.35;
const SQ_W = 2 * Math.PI * 5, SQ_Z = 0.55;
const PIN_W = 2 * Math.PI * 4.5, PIN_Z = 0.5;
/** tilt: how far it sags toward gravity, and the body's sway when the phone moves */
const SAG_MAX = 0.06, SAG_W = 2 * Math.PI * 3, SAG_Z = 0.2;
const OFF_W = 2 * Math.PI * 3, OFF_Z = 0.25;

type Dent = {
  dir: V3; D: number; Dv: number; sigma: number; sigma0: number; flat: number; lip: number;
  pressed: boolean; hold: number; pressure: number;
  /** ≥ 0 while the skin still clings to the lifted finger (s since lift), −1 otherwise */
  stickT: number;
  /** the skin pulled up by a lifted finger (fraction of the radius) and its velocity once it popped off */
  pull: number; pullV: number;
  /** after release: the springing part, its velocity, the memory part, time since the pop */
  fast: number; fastV: number; slow: number; relT: number;
  /** its finger was pulling: the stretch letting go makes the sound, not this dent */
  quiet?: boolean;
};

/**
 * A hold on the toy: the patch under the finger follows it out by `delta` and the whole body is drawn after it
 * (a rice cake pulled by one spot). After the finger lets go it draws back in slowly (a little at once, the rest
 * creeping), and can be grabbed again meanwhile.
 */
type Grab = {
  /** the grabbed surface point (toy frame, R units) and the view direction then (toy frame) */
  pt: V3; view: V3;
  /** the core the finger holds (radius, R units; well inside the fingertip): it comes along whole, the strand tapers
   * all the way down to it — a bigger rigid patch pokes out of the strand as a stub */
  sigma: number;
  /** held in two fingers: the toy splits in halves at the midpoint between them (axis toward this finger's half) */
  slab: { axis: V3; mid: V3; soft: number } | null;
  /** held in one finger: the whole body gives toward it, fading to nothing `far` from the patch's edge (the
   * distance to the body's far side) */
  field: { far: number } | null;
  /** ≥ 0 while the stretch still clings to the lifted finger (s since lift), −1 otherwise */
  stickT: number;
  /** how far it is pulled out (toy frame, R units) */
  delta: V3;
  /** the world point the finger's pull is measured from (the grabbed point where the finger took hold) */
  base: V3;
  pressed: boolean;
  fast: V3; fastV: V3; slow: V3; relT: number;
};

type Press = {
  id: number; dent: Dent; grab: Grab; x: number; y: number; vx: number; vy: number; ts: number; lastMoveAt: number; moved: number;
  /** the touch's contact area when it landed (CSS px²; 0 if the browser does not report it) */
  area0: number;
};

/** Two fingers holding it: the axis between them (toy frame) and where they started on the screen. */
type Pinch = { a: Press; b: Press; ax: V3; axS: [number, number]; gap0: number; a0: [number, number]; b0: [number, number] };

type Hit = { dir: V3; mat: V3; pt: V3 };

const isMobileDevice = () =>
  window.innerWidth < 768 || (typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches);

const norm = (v: V3): V3 => { const l = Math.hypot(v[0], v[1], v[2]) || 1; v[0] /= l; v[1] /= l; v[2] /= l; return v; };
const dot = (a: ArrayLike<number>, b: ArrayLike<number>) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const angle = (a: ArrayLike<number>, b: ArrayLike<number>) => Math.acos(Math.max(-1, Math.min(1, dot(a, b))));
const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const len = (v: ArrayLike<number>) => Math.hypot(v[0], v[1], v[2]);

type MotionPerm = { requestPermission?: () => Promise<string> };

/**
 * The 말랑이 page: one squishy toy (the mango one) you press, pull and knead with up to four fingers.
 * Fingers sink in; drag and the chunk under the finger comes out with it like slime (two fingers pull it into
 * taffy); let go and it all draws back in slowly, like memory foam. Fingers closing on it squash it between them;
 * tilting the phone makes it sag, shaking it makes it wobble. It never breaks.
 */
export class MallangEngine {
  private THREE: ThreeNS = null;
  private renderer: any; private scene: any; private camera: any; private envTex: any;
  private mesh: any; private material: any; private u!: MallangUniforms;
  private shadow: any; private shadowTex: any;
  private canvas: HTMLCanvasElement;
  private cb: MallangCallbacks;
  private audio = new MallangAudio();
  private disposed = false;
  private raf = 0; private last = 0; private acc = 0;
  private w = 1; private h = 1; private wW = 1; private wH = 1; private pxToWorld = 1;
  private maxDpr = 2; private slowFrames = 0; private startedAt = 0;
  private ro: ResizeObserver | null = null;
  /** world radius and centre */
  private R = 1; private cy = 0;
  private dents: Dent[] = [];
  private grabs: Grab[] = [];
  private presses = new Map<number, Press>();
  private spin: any; private idleAxis: any; private idleT = 2; private tq: any; private tv: any; private dq: any;
  private wob = 0; private wobV = 0; private wobAxis: V3 = [1, 0, 0];
  private sPar = 1; private sParV = 0; private sq: V3 = [0, 0, 1];
  // the pinch: fingers closing on it squeeze it along the axis between them
  private pinch: Pinch | null = null;
  private pinK = 0; private pinKV = 0; private pinAx: V3 = [1, 0, 0];
  private lastK = 0; private lastPull = 0;
  // the global affine map (the shader's malGlobal) and its inverse, for picking
  private gm: any; private gInv: any; private mInv: any; private v1: any; private v2: any; private v3: any;
  private mats = [new Float64Array(9), new Float64Array(9), new Float64Array(9)];
  // motion: gravity (world, unit; null until the phone reports it), the phone's acceleration (world, m/s²)
  private grav: V3 | null = null; private accW: V3 = [0, 0, 0]; private accAt = 0;
  private sag: V3 = [0, 0, 0]; private sagV: V3 = [0, 0, 0];
  private off: V3 = [0, 0, 0]; private offV: V3 = [0, 0, 0];
  private motionOn = false;
  private squishes = 0;
  /** per frame: [rAF time, interval, JS cost] (last 600 frames), for checking smoothness */
  frameLog: number[][] = [];

  constructor(canvas: HTMLCanvasElement, cb: MallangCallbacks) {
    this.canvas = canvas; this.cb = cb;
    this.audio.onState = (r) => cb.onAudioState(r);
  }

  async init() {
    const { THREE, RoomEnvironment } = await loadThree();
    if (this.disposed) return;
    const T = (this.THREE = THREE);
    this.maxDpr = isMobileDevice() ? 3 : 2;
    const r = (this.renderer = new T.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true, powerPreference: "high-performance" }));
    r.outputColorSpace = T.SRGBColorSpace;
    r.toneMapping = T.ACESFilmicToneMapping;
    this.scene = new T.Scene();
    this.camera = new T.PerspectiveCamera(50, 1, 0.1, 200);
    this.camera.position.set(0, 0, 20);
    const pmrem = new T.PMREMGenerator(r);
    this.envTex = pmrem.fromScene(new RoomEnvironment()).texture;
    pmrem.dispose();
    this.scene.environment = this.envTex;
    if (this.scene.environmentRotation) this.scene.environmentRotation.x = -Math.PI / 2;
    const key = new T.DirectionalLight(0xffffff, 1.9);
    key.position.set(-7, 9, 12);
    this.scene.add(key);

    // soft blob shadow on the backdrop
    const bc = document.createElement("canvas"); bc.width = bc.height = 128;
    const g = bc.getContext("2d")!, grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, "rgba(255,255,255,1)"); grd.addColorStop(0.45, "rgba(255,255,255,0.55)"); grd.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    this.shadowTex = new T.CanvasTexture(bc);
    this.shadow = new T.Mesh(new T.PlaneGeometry(1, 1), new T.MeshBasicMaterial({ map: this.shadowTex, color: 0x0f1a08, transparent: true, opacity: 0.3, depthWrite: false, toneMapped: false }));
    this.shadow.renderOrder = -1;
    this.scene.add(this.shadow);

    // the toy: a unit sphere the vertex shader shapes (mallangShader.ts)
    this.u = makeMallangUniforms(T);
    const m = (this.material = new T.MeshPhysicalMaterial({
      color: new T.Color("#6e9628"), roughness: 0.42, metalness: 0, clearcoat: 0.45, clearcoatRoughness: 0.24,
      sheen: 0.1, sheenRoughness: 0.6, sheenColor: new T.Color("#d8ecb0"), envMapIntensity: 0.65,
    }));
    m.mallangUniforms = this.u;
    m.onBeforeCompile = mallangOnBeforeCompile;
    this.mesh = new T.Mesh(new T.SphereGeometry(1, 256, 160), m);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
    this.tq = new T.Quaternion(); this.dq = new T.Quaternion(); this.tv = new T.Vector3();
    this.gm = new T.Matrix4(); this.gInv = new T.Matrix4(); this.mInv = new T.Matrix4();
    this.v1 = new T.Vector3(); this.v2 = new T.Vector3(); this.v3 = new T.Vector3();
    this.spin = new T.Vector3();
    // lying on its side like the photo, turned a little toward the light
    this.mesh.quaternion.setFromEuler(new T.Euler(0.25, -0.5, 0.18));
    this.idleAxis = new T.Vector3(0.15, 1, 0.2).normalize();

    this.resize();
    r.compile(this.scene, this.camera);
    this.attach();
    this.setupMotion();
    const idle = (window as Window & { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback ?? ((f: () => void) => setTimeout(f, 200));
    idle(() => { if (!this.disposed) this.audio.prewarm(); });
    this.last = this.startedAt = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  unlockAudio() { this.audio.unlock(); }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    const c = this.canvas;
    c.removeEventListener("pointerdown", this.onDown); c.removeEventListener("pointermove", this.onMove);
    c.removeEventListener("pointerup", this.onUp); c.removeEventListener("pointercancel", this.onUp); c.removeEventListener("lostpointercapture", this.onUp);
    c.removeEventListener("contextmenu", this.onContext);
    window.removeEventListener("resize", this.onResize); window.removeEventListener("blur", this.releaseAll);
    window.removeEventListener("deviceorientation", this.onOrientation); window.removeEventListener("devicemotion", this.onMotion);
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.mesh) { this.mesh.geometry.dispose(); this.material.dispose(); }
    if (this.shadow) { this.shadow.geometry.dispose(); this.shadow.material.dispose(); this.shadowTex.dispose(); }
    this.envTex?.dispose();
    this.renderer?.dispose();
    this.audio.dispose();
  }

  // ---------- layout ----------

  private resize() {
    const p = this.canvas.parentElement;
    const w = p?.clientWidth || window.innerWidth, h = p?.clientHeight || window.innerHeight;
    if (!(w > 0) || !(h > 0)) return;
    this.w = w; this.h = h;
    const aspect = w / h, fov = aspect > 1.5 ? (2 * Math.atan(Math.tan((25 * Math.PI) / 180) * (1.5 / aspect)) * 180) / Math.PI : 50;
    this.camera.fov = fov; this.camera.aspect = aspect; this.camera.updateProjectionMatrix();
    this.wH = 2 * Math.tan((fov * Math.PI) / 360) * 20; this.wW = this.wH * aspect; this.pxToWorld = this.wH / h;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.maxDpr));
    this.renderer.setSize(w, h, false);
    this.canvas.style.width = w + "px"; this.canvas.style.height = h + "px";
    // big enough to hold in two hands on a phone, its long axis (1.22 R) kept inside the screen, room to pull it out
    const Rpx = Math.max(60, Math.min(w * 0.33, h * 0.24, 200));
    this.R = Rpx * this.pxToWorld;
    this.cy = -0.03 * this.wH;
    this.mesh.scale.setScalar(this.R);
    this.u.uBumpH.value = 0.00035 * this.R;
    this.placeMesh();
  }

  private placeMesh() { this.mesh.position.set(this.off[0], this.cy + this.off[1], this.off[2]); }

  private onResize = () => {
    const p = this.canvas.parentElement;
    const w = p?.clientWidth || window.innerWidth, h = p?.clientHeight || window.innerHeight;
    if (w === this.w && h === this.h) return;
    this.releaseAll(); this.resize();
  };

  // ---------- frames ----------

  private toLocal(w: ArrayLike<number>, out: V3 = [0, 0, 0]): V3 {
    this.tq.copy(this.mesh.quaternion).invert();
    this.tv.set(w[0], w[1], w[2]).applyQuaternion(this.tq);
    out[0] = this.tv.x; out[1] = this.tv.y; out[2] = this.tv.z;
    return out;
  }

  private worldOf(l: ArrayLike<number>): V3 {
    this.tv.set(l[0], l[1], l[2]).applyQuaternion(this.mesh.quaternion);
    return [this.tv.x, this.tv.y, this.tv.z];
  }

  /** A point of the toy (toy frame, R units) in world space. */
  private toWorldPoint(p: ArrayLike<number>): V3 {
    this.mesh.updateMatrixWorld();
    const v = this.v3.set(p[0], p[1], p[2]).applyMatrix4(this.mesh.matrixWorld);
    return [v.x, v.y, v.z];
  }

  /** Where the camera ray through screen point (x, y) crosses the plane z = zw (world). */
  private screenToPlane(x: number, y: number, zw: number): V3 {
    const wx = ((x / this.w) * 2 - 1) * (this.wW / 2), wy = -((y / this.h) * 2 - 1) * (this.wH / 2);
    const t = (20 - zw) / 20; // the camera sits at z = 20 looking at z = 0
    return [wx * t, wy * t, zw];
  }

  /** A world point on the screen (CSS px). */
  private toScreen2(p: ArrayLike<number>): [number, number] {
    const v = this.v3.set(p[0], p[1], p[2]).project(this.camera);
    return [((v.x + 1) / 2) * this.w, ((1 - v.y) / 2) * this.h];
  }

  /**
   * The shader's malGlobal as a matrix (toy frame): the pinch squeeze along A, then the one-sided squash toward the
   * screen along q.
   */
  private globalMatrix() {
    const A = this.pinAx, k = this.pinK, q = this.sq, sPar = this.sPar, sPerp = 1 + 0.5 * (1 - this.sPar);
    // P = (1 + k/2) I − 1.5 k A Aᵀ ; Q = sPerp I + (sPar − sPerp) q qᵀ, t = (sPar − 1) q
    const [P, Q, L] = this.mats;
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
      const id = i === j ? 1 : 0;
      P[i * 3 + j] = (1 + 0.5 * k) * id - 1.5 * k * A[i] * A[j];
      Q[i * 3 + j] = sPerp * id + (sPar - sPerp) * q[i] * q[j];
    }
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) { let a = 0; for (let n = 0; n < 3; n++) a += Q[i * 3 + n] * P[n * 3 + j]; L[i * 3 + j] = a; }
    const t = sPar - 1;
    this.gm.set(L[0], L[1], L[2], t * q[0], L[3], L[4], L[5], t * q[1], L[6], L[7], L[8], t * q[2], 0, 0, 0, 1);
    this.gInv.copy(this.gm).invert();
  }

  /**
   * The point of the toy under screen point (x, y), or null if the touch is off it: its direction from the centre
   * in the deformed space (where dents live), its material direction (where grabs centre) and the point itself (toy
   * frame, R units). The camera ray is taken back through the hands' squeeze (the affine map) and intersected with
   * the toy's ellipsoid (dents and pulled chunks ignored: close enough).
   */
  private pick(x: number, y: number, loose = false): Hit | null {
    const wx = ((x / this.w) * 2 - 1) * (this.wW / 2), wy = -((y / this.h) * 2 - 1) * (this.wH / 2);
    this.mesh.updateMatrixWorld();
    this.mInv.copy(this.mesh.matrixWorld).invert();
    const o = this.v1.set(0, 0, 20).applyMatrix4(this.mInv).applyMatrix4(this.gInv);
    const t = this.v2.set(wx, wy, 0).applyMatrix4(this.mInv).applyMatrix4(this.gInv);
    const ax = this.u.uAxes.value as any;
    o.divide(ax); t.divide(ax);
    const d = t.sub(o).normalize();
    const b = o.dot(d), c = o.dot(o) - 1, disc = b * b - c;
    const s = disc >= 0 ? -b - Math.sqrt(disc) : -b;
    let hx = o.x + d.x * s, hy = o.y + d.y * s, hz = o.z + d.z * s;
    // just off the silhouette: the rim there (a finger sliding off keeps hold; a fresh touch must be closer)
    if (disc < 0 && Math.hypot(hx, hy, hz) > (loose ? 1.6 : 1.08)) return null;
    const l = Math.hypot(hx, hy, hz) || 1; hx /= l; hy /= l; hz /= l;
    // that point on the egg, then squeezed
    const egg = this.u.uEgg.value as number, f = 1 - egg * hx;
    const p = this.v1.set(hx * ax.x, hy * ax.y * f, hz * ax.z * f).applyMatrix4(this.gm);
    const pt: V3 = [p.x, p.y, p.z];
    return { dir: norm([p.x, p.y, p.z]), mat: [hx, hy, hz], pt };
  }

  // ---------- input ----------

  private attach() {
    const c = this.canvas;
    c.addEventListener("contextmenu", this.onContext);
    c.addEventListener("pointerdown", this.onDown);
    c.addEventListener("pointermove", this.onMove);
    c.addEventListener("pointerup", this.onUp);
    c.addEventListener("pointercancel", this.onUp);
    c.addEventListener("lostpointercapture", this.onUp);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("blur", this.releaseAll);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.ro = new ResizeObserver(this.onResize);
    if (c.parentElement) this.ro.observe(c.parentElement);
  }
  private onContext = (e: Event) => e.preventDefault();
  private onVisibility = () => { if (document.hidden) this.releaseAll(); else this.audio.resumeExisting(); };
  private pos(e: PointerEvent): [number, number] { const r = this.canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; }

  /** A touch's contact size (CSS px) as the browser reports it, or the usual fingertip. */
  private contactOf(e: PointerEvent) {
    const w = e.width || 0, h = e.height || 0;
    return e.pointerType === "touch" && w > 2 && h > 2 ? clamp(0.6 * (w + h), 26, 70) : CONTACT_PX;
  }

  /** A chunk still drawing back in whose tip is under (x, y): a finger there takes hold of it again. */
  private regrabAt(x: number, y: number): Grab | null {
    const Rpx = this.R / this.pxToWorld;
    let best: Grab | null = null, bd = 0.42 * Rpx;
    for (const g of this.grabs) {
      if (g.pressed || len(g.delta) < 0.12) continue;
      const tip = this.toWorldPoint([g.pt[0] + g.delta[0], g.pt[1] + g.delta[1], g.pt[2] + g.delta[2]]);
      const [sx, sy] = this.toScreen2(tip), dd = Math.hypot(sx - x, sy - y);
      if (dd < bd) { bd = dd; best = g; }
    }
    return best;
  }

  private onDown = (e: PointerEvent) => {
    this.audio.wanted = true; this.audio.unlock();
    if (this.presses.size >= MAX_PRESS) return;
    const [x, y] = this.pos(e);
    this.globalMatrix();
    // a pulled-out chunk still drawing back in can be caught again and pulled further
    let grab = this.regrabAt(x, y);
    let hit: Hit | null = null;
    if (grab) {
      hit = { dir: norm([grab.pt[0], grab.pt[1], grab.pt[2]]), mat: [0, 0, 1], pt: grab.pt };
    } else {
      hit = this.pick(x, y);
      if (!hit) return;
    }
    try { this.canvas.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
    const Rpx = this.R / this.pxToWorld, s0 = clamp((0.95 * this.contactOf(e)) / Rpx, 0.15, 0.42);
    // a finger pressing right where a mark is still filling in picks that dent up again (no double dent)
    let dent: Dent | null = null;
    for (const d of this.dents) if (!d.pressed && angle(d.dir, hit.dir) < 0.6 * d.sigma) { dent = d; break; }
    if (dent) {
      dent.pressed = true; dent.stickT = -1; dent.hold = Math.max(0, dent.D) * 0.6; dent.Dv = dent.fastV;
      dent.dir = hit.dir; dent.sigma0 = s0;
    } else {
      dent = {
        dir: hit.dir, D: 0, Dv: 0, sigma: s0, sigma0: s0, flat: 0, lip: 0.3, pressed: true, hold: 0, pressure: e.pointerType === "pen" ? 0.6 + e.pressure : 1,
        stickT: -1, pull: 0, pullV: 0, fast: 0, fastV: 0, slow: 0, relT: 0,
      };
      this.dents.unshift(dent);
      this.trimDents();
    }
    // the chunk the finger takes hold of: the pull is measured from where the finger is now, so a caught chunk
    // carries on from where it was
    if (grab) {
      const tipW = this.toWorldPoint([grab.pt[0] + grab.delta[0], grab.pt[1] + grab.delta[1], grab.pt[2] + grab.delta[2]]);
      const at = this.screenToPlane(x, y, tipW[2]), dW = this.worldOf(grab.delta).map((v) => v * this.R);
      grab.base = [at[0] - dW[0], at[1] - dW[1], at[2] - dW[2]];
      grab.pressed = true; grab.relT = 0; grab.stickT = -1;
    } else {
      const ptW = this.toWorldPoint(hit.pt);
      grab = {
        pt: hit.pt, view: this.toLocal([0, 0, 1]), sigma: clamp(0.5 * s0, 0.08, 0.2), slab: null, field: null, stickT: -1,
        delta: [0, 0, 0], base: ptW, pressed: true, fast: [0, 0, 0], fastV: [0, 0, 0], slow: [0, 0, 0], relT: 0,
      };
      this.fingerField(grab);
      this.grabs.unshift(grab);
      this.trimGrabs();
    }
    const now = performance.now();
    const area0 = e.pointerType === "touch" && e.width > 2 && e.height > 2 ? e.width * e.height : 0;
    this.presses.set(e.pointerId, { id: e.pointerId, dent, grab, x, y, vx: 0, vy: 0, ts: e.timeStamp, lastMoveAt: now, moved: 0, area0 });
    this.audio.press(this.pitch());
    vibrate(8);
    e.preventDefault();
  };

  private onMove = (e: PointerEvent) => {
    const p = this.presses.get(e.pointerId); if (!p) return;
    const [x, y] = this.pos(e);
    const dt = Math.max(1, e.timeStamp - p.ts) / 1000; p.ts = e.timeStamp;
    const k = Math.min(1, dt / 0.04);
    p.vx += ((x - p.x) / dt - p.vx) * k; p.vy += ((y - p.y) / dt - p.vy) * k;
    p.moved += Math.hypot(x - p.x, y - p.y);
    p.x = x; p.y = y; p.lastMoveAt = performance.now();
    // a fingertip pressed harder flattens: a bigger contact patch reads as more pressure
    if (p.area0 > 0 && e.width > 2 && e.height > 2) {
      const grow = Math.sqrt((e.width * e.height) / p.area0);
      p.dent.pressure += (clamp(1 + 0.8 * (grow - 1), 0.85, 1.35) - p.dent.pressure) * 0.3;
    }
  };

  private onUp = (e: PointerEvent) => {
    this.audio.unlock();
    const p = this.presses.get(e.pointerId); if (!p) return;
    this.presses.delete(e.pointerId);
    this.release(p);
  };

  private releaseAll = () => { for (const p of [...this.presses.values()]) this.release(p); this.presses.clear(); };

  /** The finger lifts: the sticky cake clings to it a moment (it lets go in springs()), then creeps back in. */
  private release(p: Press) {
    const d = p.dent, g = p.grab;
    if (d.pressed) { d.pressed = false; d.stickT = 0; d.quiet = len(g.delta) > 0.15; }
    if (g.pressed) { g.pressed = false; g.stickT = 0; }
    this.audio.rubEnd(p.id);
    if (this.pinch && (this.pinch.a === p || this.pinch.b === p)) this.pinch = null;
  }

  /** The stretch lets go of the finger ("쩍"): a little springs back, the rest creeps back in. */
  private letGo(g: Grab) {
    g.stickT = -1; g.relT = 0;
    for (let i = 0; i < 3; i++) { g.fast[i] = g.delta[i] * GRAB_FAST; g.slow[i] = g.delta[i] * (1 - GRAB_FAST); g.fastV[i] = 0; }
    const L = len(g.delta);
    if (L > 0.15) {
      this.audio.release(0.12 + 0.18 * Math.min(1, L / PULL_MAX), this.pitch());
      this.squishes++; this.cb.onSquish(this.squishes);
      vibrate(8);
    }
  }

  /** One finger's hold on the whole body: the give fades out over the distance from the patch's edge to the far side. */
  private fingerField(g: Grab) {
    const ax = this.u.uAxes.value as any;
    const n = norm([...g.pt] as V3);
    const reach = 1 / Math.sqrt((n[0] / ax.x) ** 2 + (n[1] / ax.y) ** 2 + (n[2] / ax.z) ** 2); // the body's extent opposite
    g.field = { far: Math.max(0.3, len(g.pt) + reach - g.sigma) };
  }

  /** The skin lets go of the finger: "뽕", the body rings, the dent starts filling in. */
  private pop(d: Dent) {
    d.stickT = -1;
    d.fast = d.D * FAST_SHARE; d.fastV = 0;
    d.slow = d.D * (1 - FAST_SHARE); d.relT = 0;
    if (d.quiet) { d.quiet = false; return; }
    if (d.D > 0.02) {
      this.squishes++; this.cb.onSquish(this.squishes);
      vibrate(6);
    }
    this.audio.release(d.D, this.pitch());
  }

  private pitch() { return Math.max(0.75, Math.min(1.2, 140 / (this.R / this.pxToWorld))); }

  /** At most MAX_DENTS (one slot kept for the pinch ridge): pressed ones stay; the shallowest filling-in marks go. */
  private trimDents() {
    while (this.dents.length > MAX_DENTS - 1) {
      let k = -1, best = Infinity;
      for (let i = 0; i < this.dents.length; i++) { const d = this.dents[i]; if (!d.pressed && d.stickT < 0 && Math.abs(d.D) < best) { best = Math.abs(d.D); k = i; } }
      if (k < 0) break;
      this.dents.splice(k, 1);
    }
  }

  /** At most MAX_GRABS: held ones stay; the least pulled-out of the rest go. */
  private trimGrabs() {
    while (this.grabs.length > MAX_GRABS) {
      let k = -1, best = Infinity;
      for (let i = 0; i < this.grabs.length; i++) { const g = this.grabs[i]; if (!g.pressed && len(g.delta) < best) { best = len(g.delta); k = i; } }
      if (k < 0) break;
      this.grabs.splice(k, 1);
    }
  }

  // ---------- motion (tilt and shake) ----------

  /**
   * Listens right away. Where the browser has a permission call (iPhone; newer Chrome has one too but needs no
   * answer), the "흔들기 켜기" chip shows until real sensor data arrives — on iPhone that is after the tap.
   */
  private setupMotion() {
    if (typeof window === "undefined" || !("DeviceMotionEvent" in window)) return;
    this.listenMotion();
    const DM = (window as unknown as { DeviceMotionEvent: MotionPerm }).DeviceMotionEvent;
    if (typeof DM.requestPermission === "function" && isMobileDevice()) { this.motionAsked = true; this.cb.onMotionNeeded(true); }
  }
  private motionAsked = false;
  /** sensor data arrived: no tap needed (any more) */
  private motionWorks() { if (this.motionAsked) { this.motionAsked = false; this.cb.onMotionNeeded(false); } }

  /** From a tap: asks iOS for motion access, then listens. */
  async enableMotion() {
    const W = window as unknown as { DeviceMotionEvent?: MotionPerm; DeviceOrientationEvent?: MotionPerm };
    try {
      const a = W.DeviceMotionEvent?.requestPermission ? await W.DeviceMotionEvent.requestPermission() : "granted";
      if (W.DeviceOrientationEvent?.requestPermission) await W.DeviceOrientationEvent.requestPermission();
      if (a === "granted") this.listenMotion();
    } catch { /* refused or unsupported: it just stays put */ }
    this.motionAsked = false;
    this.cb.onMotionNeeded(false);
  }

  private listenMotion() {
    if (this.motionOn || this.disposed) return;
    this.motionOn = true;
    window.addEventListener("deviceorientation", this.onOrientation);
    window.addEventListener("devicemotion", this.onMotion);
  }

  /** Device axes (x right, y up, z out of the screen) → the screen's, as the screen is turned. */
  private toScreen(x: number, y: number, z: number): V3 {
    const so = (screen as Screen & { orientation?: { angle: number } }).orientation;
    const a = ((so?.angle ?? (window as unknown as { orientation?: number }).orientation ?? 0) * Math.PI) / 180;
    const c = Math.cos(a), s = Math.sin(a);
    return [x * c - y * s, x * s + y * c, z];
  }

  private onOrientation = (e: DeviceOrientationEvent) => {
    if (e.beta == null || e.gamma == null) return;
    const b = (e.beta * Math.PI) / 180, g = (e.gamma * Math.PI) / 180;
    // gravity in the device frame for the W3C Z-X'-Y'' angles: (cosβ sinγ, −sinβ, −cosβ cosγ)
    this.grav = this.toScreen(Math.cos(b) * Math.sin(g), -Math.sin(b), -Math.cos(b) * Math.cos(g));
    this.motionWorks();
  };

  private onMotion = (e: DeviceMotionEvent) => {
    const a = e.acceleration;
    if (!a || a.x == null || a.y == null || a.z == null) return;
    this.accW = this.toScreen(a.x, a.y, a.z);
    this.accAt = performance.now();
    this.motionWorks();
  };

  // ---------- physics ----------

  private step(dt: number) {
    const now = performance.now();
    const n = this.presses.size;
    // the toy turns slowly by itself when left alone; held, it stays put (a dragging finger pulls, it does not roll)
    const s = this.spin;
    if (n > 0) {
      s.multiplyScalar(Math.exp(-dt / 0.06));
      this.idleT = 0;
    } else {
      this.idleT += dt;
      const ramp = Math.max(0, Math.min(1, (this.idleT - 2.5) / 2)), ax = this.idleAxis;
      ax.x += (Math.random() - 0.5) * 0.01; ax.z += (Math.random() - 0.5) * 0.01; ax.normalize();
      const w = 0.16 * ramp, k = 1 - Math.exp(-dt / 1.2);
      s.x += (ax.x * w - s.x) * k; s.y += (ax.y * w - s.y) * k; s.z += (ax.z * w - s.z) * k;
    }
    const a = s.length() * dt;
    if (a > 1e-7) { this.tv.copy(s).normalize(); this.dq.setFromAxisAngle(this.tv, a); this.mesh.quaternion.premultiply(this.dq); }

    this.globalMatrix();
    // two fingers: closing on it squeezes it between them; each pulls a chunk as wide as half the gap (taffy)
    const kT = this.pinchTargets();
    let pressDepth = 0, sqx = 0, sqy = 0, sqz = 0, pullTotal = 0;
    const pullW: V3 = [0, 0, 0];
    for (const p of this.presses.values()) {
      const d = p.dent, g = p.grab;
      // the finger pulls its chunk: from where it took hold to where it is now, harder the further it goes
      const T = this.screenToPlane(p.x, p.y, g.base[2]);
      const rw: V3 = [T[0] - g.base[0], T[1] - g.base[1], T[2] - g.base[2]];
      const raw = this.toLocal(rw).map((v) => v / this.R) as V3;
      const Lr = len(raw), L = PULL_MAX * Math.tanh(Lr / PULL_MAX);
      const tgt: V3 = Lr > 1e-6 ? [raw[0] * (L / Lr), raw[1] * (L / Lr), raw[2] * (L / Lr)] : [0, 0, 0];
      // the heavy, sticky cake follows a little behind the finger
      const k = 1 - Math.exp(-dt / PULL_LAG);
      for (let i = 0; i < 3; i++) g.delta[i] += (tgt[i] - g.delta[i]) * k;
      if (!g.slab) this.fingerField(g);
      pullTotal += len(g.delta);
      for (let i = 0; i < 3; i++) pullW[i] += rw[i];
      this.audio.rub(p.id, now - p.lastMoveAt < 60 ? Math.hypot(p.vx, p.vy) : 0, Math.max(d.D, 0.1 * len(g.delta)));
      pressDepth = Math.max(pressDepth, d.D);
      // the hand pushes the toy toward the screen along the press: aimed in world space (never tilts with the toy)
      const wd = this.worldOf(d.dir);
      sqx += wd[0] * 1.6; sqy += wd[1] * 1.6; sqz += wd[2] * 0.7;
    }
    if (n > 0) {
      const sl = this.toLocal(norm([sqx, sqy, sqz])), k = 1 - Math.exp(-dt / 0.08);
      this.sq[0] += (sl[0] - this.sq[0]) * k; this.sq[1] += (sl[1] - this.sq[1]) * k; this.sq[2] += (sl[2] - this.sq[2]) * k; norm(this.sq);
    }
    // pulling out creaks and stretches audibly as it lengthens
    if (pullTotal > this.lastPull + 0.004) this.audio.sink((pullTotal - this.lastPull) * 0.08, 0.3, this.pitch() * 0.85);
    this.lastPull = pullTotal;
    // more than two fingers: the whole hand closes on it
    const grip = Math.max(0, n - 2);

    // tilt: gravity, as the toy sees it; the phone's push (a held toy lags behind the hand); a pulled toy comes along a bit
    const fresh = now - this.accAt < 120;
    const accW: V3 = fresh ? this.accW : [0, 0, 0];
    const accMag = Math.hypot(accW[0], accW[1], accW[2]);
    const accEff = accMag < 0.4 ? 0 : 1; // the sensor's jitter when the phone is held still
    const sagT: V3 = this.grav ? this.toLocal(this.grav).map((v) => v * SAG_MAX) as V3 : [0, 0, 0];
    const offT: V3 = this.grav ? [this.grav[0] * 0.05 * this.R, this.grav[1] * 0.05 * this.R, 0] : [0, 0, 0];
    if (n > 0) { offT[0] += 0.1 * pullW[0] / n; offT[1] += 0.1 * pullW[1] / n; }
    const accL = accMag > 2 ? this.toLocal([accW[0] / accMag, accW[1] / accMag, accW[2] / accMag]) : null;
    if (accL) { const k = 1 - Math.exp(-dt / 0.2); for (let i = 0; i < 3; i++) this.wobAxis[i] += (accL[i] - this.wobAxis[i]) * k; norm(this.wobAxis); }
    const accAlong = accL ? accMag * dot(accL, this.wobAxis) : 0;

    // substeps for the springs
    const steps = Math.max(1, Math.round(dt / H)), h = dt / steps;
    for (let i = 0; i < steps; i++) this.springs(h, pressDepth, kT, grip, sagT, offT, accW, accEff, accAlong);

    // marks that have filled back in, and chunks that have drawn back in, go away
    this.dents = this.dents.filter((d) => d.pressed || d.stickT >= 0 || d.relT < 0.4 || Math.abs(d.D) > 0.0015 || Math.abs(d.fastV) > 0.02 || Math.abs(d.pull) > 0.001);
    this.grabs = this.grabs.filter((g) => g.pressed || g.stickT >= 0 || g.relT < 0.5 || len(g.delta) > 0.01 || len(g.fastV) > 0.02);
    this.placeMesh();
  }

  /** The pinch squeeze target from where the two fingers have moved since they took hold; sets the chunks' widths. */
  private pinchTargets(): number {
    const ps = [...this.presses.values()];
    if (ps.length < 2) { this.pinch = null; return 0; }
    const a = ps[0], b = ps[1];
    if (!this.pinch || this.pinch.a !== a || this.pinch.b !== b) {
      const gx = b.x - a.x, gy = b.y - a.y, gap0 = Math.max(30, Math.hypot(gx, gy));
      const ax = norm([b.dent.dir[0] - a.dent.dir[0], b.dent.dir[1] - a.dent.dir[1], b.dent.dir[2] - a.dent.dir[2]]);
      this.pinch = { a, b, ax, axS: [gx / gap0, gy / gap0], gap0, a0: [a.x, a.y], b0: [b.x, b.y] };
      this.pinAx = [...ax];
      this.pinK = 0; this.pinKV = 0;
      // held in two fingers, the toy splits into halves at the midpoint: each half follows its finger (taffy)
      const ga = a.grab, gb = b.grab;
      const dp: V3 = [gb.pt[0] - ga.pt[0], gb.pt[1] - ga.pt[1], gb.pt[2] - ga.pt[2]], half = len(dp) / 2;
      if (half > 0.15) {
        // the split sits halfway between the fingers, on the body's own centre line: the strand thins toward its core
        // (toward the skin under the fingers, the back would fold through the front)
        const axis = norm([...dp] as V3), m = (dot(ga.pt, axis) + dot(gb.pt, axis)) / 2;
        const mid: V3 = [axis[0] * m, axis[1] * m, axis[2] * m];
        // the ramp runs a little past each finger: the strand between them stretches as a whole, and the lumps in
        // the fingers round off into it (a ramp ending right at the finger makes a hard shoulder)
        const soft = clamp(1.5 * half, 0.3, 1.2);
        ga.slab = { axis: [-axis[0], -axis[1], -axis[2]], mid, soft };
        gb.slab = { axis, mid, soft };
      }
    }
    const P = this.pinch;
    const rx = (b.x - P.b0[0]) - (a.x - P.a0[0]), ry = (b.y - P.b0[1]) - (a.y - P.a0[1]);
    const r = 1 + (rx * P.axS[0] + ry * P.axS[1]) / P.gap0; // < 1 closing
    const depth = Math.min(a.dent.D, b.dent.D) / DMAX;
    return clamp(0.08 * depth + (r < 1 ? 0.55 * (1 - r) : 0), 0, 0.26);
  }

  private springs(h: number, pressDepth: number, kT: number, grip: number, sagT: V3, offT: V3, accW: V3, accEff: number, accAlong: number) {
    let sinkTotal = 0, deepest = 0;
    for (const d of this.dents) {
      if (d.pressed) {
        // 쫀득: the finger sinks in fast at first, then the skin pushes back harder the deeper it goes
        d.hold += h;
        const tgt = DMAX * clamp(d.sigma0 / 0.22, 0.65, 1.15) * d.pressure * (1 - Math.exp(-d.hold / 0.38));
        const w = 2 * Math.PI * 3.5, z = 1;
        const D0 = d.D;
        d.Dv += (w * w * (tgt - d.D) - 2 * z * w * d.Dv) * h;
        d.D += d.Dv * h;
        if (d.D > D0) sinkTotal += d.D - D0;
        deepest = Math.max(deepest, d.D);
      } else if (d.stickT >= 0) {
        // the skin still clings to the lifted finger: pulled up a little in the middle, then it pops off
        d.stickT += h;
        const u = Math.min(1, d.stickT / STICK_T);
        d.pull = PULL_K * Math.max(0, d.D) * u * u * (3 - 2 * u);
        if (d.stickT >= STICK_T) this.pop(d);
      } else {
        // let go: a small share draws back at once (no ring), the rest refills slowly like memory foam
        d.relT += h;
        d.fastV += (-FAST_W * FAST_W * d.fast - 2 * FAST_Z * FAST_W * d.fastV) * h;
        d.fast += d.fastV * h;
        const go = 0.25 + 0.75 * Math.min(1, d.relT / SLOW_START);
        d.slow *= Math.exp((-h / SLOW_TAU) * go);
        d.D = d.slow + d.fast;
        d.pullV += (-PULL_W * PULL_W * d.pull - 2 * PULL_Z * PULL_W * d.pullV) * h;
        d.pull += d.pullV * h;
      }
      // a deeper press flattens into a fingertip pad and spreads wider; a filling mark rounds off again
      const f = Math.min(1, Math.max(0, d.D) / 0.12);
      d.flat += ((d.pressed ? 0.95 * f : 0.25) - d.flat) * Math.min(1, h / 0.08);
      d.sigma = d.sigma0 * (1 + 0.6 * Math.max(0, d.D) / DMAX);
    }
    // holds let go: a small share draws back at once, the rest creeps back in slowly (memory foam)
    for (const g of this.grabs) {
      if (g.pressed) continue;
      if (g.stickT >= 0) { g.stickT += h; if (g.stickT >= GRAB_STICK) this.letGo(g); continue; }
      g.relT += h;
      const go = 0.25 + 0.75 * Math.min(1, g.relT / GRAB_START), decay = Math.exp((-h / GRAB_TAU) * go);
      for (let i = 0; i < 3; i++) {
        g.fastV[i] += (-GRAB_W * GRAB_W * g.fast[i] - 2 * GRAB_Z * GRAB_W * g.fastV[i]) * h;
        g.fast[i] += g.fastV[i] * h;
        g.slow[i] *= decay;
        g.delta[i] = g.fast[i] + g.slow[i];
      }
    }
    // the whole body: squashed toward the screen while pressed (harder with the whole hand), springing back past it
    const sTar = 1 - 0.55 * pressDepth - 0.06 * grip;
    this.sParV += (SQ_W * SQ_W * (sTar - this.sPar) - 2 * SQ_Z * SQ_W * this.sParV) * h;
    this.sPar += this.sParV * h;
    // the pinch's squeeze (when the hold ends, it springs back with a bounce)
    this.pinKV += (PIN_W * PIN_W * (kT - this.pinK) - 2 * PIN_Z * PIN_W * this.pinKV) * h;
    this.pinK = clamp(this.pinK + this.pinKV * h, -0.1, 0.32);
    const ak = Math.max(0, this.pinK);
    if (ak > this.lastK + 1e-4) sinkTotal += 0.5 * (ak - this.lastK);
    this.lastK = ak;
    if (sinkTotal > 0) this.audio.sink(sinkTotal, Math.max(deepest, ak), this.pitch());
    // the body's jiggle, also driven by the phone being shaken (along the shake's axis)
    this.wobV += (-WOB_W * WOB_W * this.wob - 2 * WOB_Z * WOB_W * this.wobV - 2.6 * accAlong * accEff) * h;
    this.wob = clamp(this.wob + this.wobV * h, -0.08, 0.08);
    // tilt: it sags toward gravity and slides a little that way; moving the phone sways it (it lags behind)
    const kA = 2.4 * this.R * accEff;
    for (let i = 0; i < 3; i++) {
      this.sagV[i] += (SAG_W * SAG_W * (sagT[i] - this.sag[i]) - 2 * SAG_Z * SAG_W * this.sagV[i]) * h;
      this.sag[i] += this.sagV[i] * h;
      this.offV[i] += (OFF_W * OFF_W * (offT[i] - this.off[i]) - 2 * OFF_Z * OFF_W * this.offV[i] - kA * accW[i]) * h;
      this.off[i] = clamp(this.off[i] + this.offV[i] * h, -0.2 * this.R, 0.2 * this.R);
    }
  }

  private upload() {
    const u = this.u, A = u.uDA.value as any[], B = u.uDB.value as any[];
    // pressed dents first (they never get dropped)
    const list = this.dents.slice().sort((a, b) => Number(b.pressed) - Number(a.pressed));
    // two fingers closing on it: the skin caught between them is squeezed up into a ridge (a dent of negative depth
    // at their midpoint, as wide as the gap, as high as the shallower press)
    let ridge: [number, number, number, number, number] | null = null;
    if (this.pinch && this.pinK > 0.03) {
      const a = this.pinch.a.dent, b = this.pinch.b.dent, gap = angle(a.dir, b.dir);
      if (gap > 0.25 && gap < 1.6) {
        const m = norm([a.dir[0] + b.dir[0], a.dir[1] + b.dir[1], a.dir[2] + b.dir[2]]);
        const k = Math.min(1, (gap - 0.25) / 0.2) * Math.min(1, (1.6 - gap) / 0.4) * Math.min(1, this.pinK / 0.1);
        ridge = [m[0], m[1], m[2], -1.0 * Math.min(a.D, b.D) * k, Math.max(0.14, 0.42 * gap)];
      }
    }
    const n = Math.min(MAX_DENTS - (ridge ? 1 : 0), list.length);
    let vol = 0;
    // a strand being pulled out: the finger pinches rather than presses, so its dent goes away (a bowl deeper than
    // the strand's tip is wide would notch it, or fold it through)
    const pullAt = (d: Dent) => { for (const p of this.presses.values()) if (p.dent === d) return len(p.grab.delta); return 0; };
    for (let i = 0; i < n; i++) {
      const d = list[i], ease = d.pressed ? 1 - Math.min(1, pullAt(d) / 0.4) : 1;
      A[i].set(d.dir[0], d.dir[1], d.dir[2], d.D * ease);
      B[i].set(d.sigma, d.flat, d.lip, d.pull);
      vol += Math.max(0, d.D * ease) * (d.sigma / 0.3) * (d.sigma / 0.3);
    }
    if (ridge) { A[n].set(ridge[0], ridge[1], ridge[2], ridge[3]); B[n].set(ridge[4], 0, 0, 0); }
    u.uDN.value = n + (ridge ? 1 : 0);
    // the pulled chunks: how far out, how thin the neck, how much it sags
    const GA = u.uGA.value as any[], GB = u.uGB.value as any[], GC = u.uGC.value as any[];
    const ng = Math.min(MAX_GRABS, this.grabs.length);
    let pulled = 0;
    for (let i = 0; i < ng; i++) {
      const g = this.grabs[i], L = len(g.delta);
      if (g.slab) {
        // two fingers' halves share one strand, and both thin and sag it
        const r = g.slab;
        GA[i].set(r.axis[0], r.axis[1], r.axis[2], -r.soft);
        GB[i].set(g.delta[0], g.delta[1], g.delta[2], 0.48 * Math.min(1, L / 1.3));
        GC[i].set(r.mid[0], r.mid[1], r.mid[2], 0.14 * L);
      } else {
        const f = g.field ?? (this.fingerField(g), g.field!);
        // the last bit of a drawn-in strand fades out (a tiny pull still bends the shading a little)
        const k = Math.min(1, L / 0.06);
        GA[i].set(g.view[0], g.view[1], g.view[2], f.far);
        GB[i].set(g.delta[0] * k, g.delta[1] * k, g.delta[2] * k, STRAND_SAG * L * k);
        GC[i].set(g.pt[0], g.pt[1], g.pt[2], g.sigma);
      }
      pulled += L;
    }
    u.uGN.value = ng;
    // which way a hanging strand sags: down the screen (or toward gravity, when the phone reports it)
    const down = this.toLocal(this.grav ?? [0, -1, 0]);
    (u.uDown.value as any).set(down[0], down[1], down[2]);
    // the volume the fingers push in comes out everywhere else; what is pulled out comes from the body
    u.uVol.value = clamp(0.2 * vol - 0.03 * pulled, -0.06, 0.09);
    (u.uPA.value as any).set(this.pinAx[0], this.pinAx[1], this.pinAx[2], this.pinK);
    (u.uSq.value as any).set(this.sq[0], this.sq[1], this.sq[2]);
    u.uSPar.value = this.sPar; u.uSPerp.value = 1 + 0.5 * (1 - this.sPar);
    (u.uWob.value as any).set(this.wobAxis[0], this.wobAxis[1], this.wobAxis[2], this.wob);
    const sl = len(this.sag);
    if (sl > 1e-5) (u.uGrav.value as any).set(this.sag[0] / sl, this.sag[1] / sl, this.sag[2] / sl, sl);
    else (u.uGrav.value as any).w = 0;
    // the shadow follows the body, darkens and spreads a little under a press
    const press = Math.min(1, Math.max(0, u.uVol.value as number) / 0.05);
    this.shadow.position.set(0.12 * this.R + this.off[0] * 0.6, this.cy - 0.24 * this.R + this.off[1] * 0.6, -this.R);
    this.shadow.scale.set(3.1 * this.R * (1 + 0.08 * press + 0.25 * Math.max(0, this.pinK) + 0.2 * Math.min(1, pulled)), 2.3 * this.R * (1 + 0.05 * press), 1);
    this.shadow.material.opacity = 0.3 * (1 + 0.6 * press);
  }

  private frame = (now: number) => {
    if (this.disposed) return;
    const t0 = performance.now();
    const interval = now - this.last;
    const dt = Math.max(0, Math.min(interval / 1000, 0.05)); this.last = now;
    this.acc += dt;
    // physics in fixed 1/60 s steps (the springs substep inside)
    let steps = 0;
    while (this.acc >= 1 / 60 && steps < 4) { this.step(1 / 60); this.acc -= 1 / 60; steps++; }
    if (steps === 4) this.acc = 0;
    this.upload();
    this.renderer.render(this.scene, this.camera);
    this.frameLog.push([now, interval, performance.now() - t0]);
    if (this.frameLog.length > 600) this.frameLog.splice(0, 100);
    this.governDpr(now, interval);
    this.raf = requestAnimationFrame(this.frame);
  };

  /** A phone that cannot hold ~60 fps at its full 3× density drops to 2× for the rest of the visit. */
  private governDpr(now: number, interval: number) {
    if (this.maxDpr <= 2 || this.renderer.getPixelRatio() <= 2 || now - this.startedAt < 3000 || interval > 120) return;
    this.slowFrames = interval > 21 ? this.slowFrames + 1 : Math.max(0, this.slowFrames - 1);
    if (this.slowFrames > 45) { this.slowFrames = 0; this.maxDpr = 2; this.resize(); }
  }
}
